import { useState } from 'react';
import { Alert, App, Button, Card, Spin, Typography } from 'antd';
import type { SetDoohBroadcastBody } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { useAuth } from '@/features/auth/context/AuthContext';
import { canManageDoohMedia } from '@/layouts/navigation';
import { formatDateTime, formatItemCount } from '../utils/format';
import { doohApi } from '../api/doohApi';
import { BroadcastComposer } from '../components/BroadcastComposer';
import { DoohFlowHint } from '../components/DoohFlowHint';
import { useDoohBroadcast } from '../hooks/useDoohBroadcast';
import { useDoohMedia } from '../hooks/useDoohMedia';

/**
 * Emisión global DOOH (TG-59, `/dooh/emision`): ver la playlist en emisión y
 * componer/publicar la playlist SMIL única para toda la red.
 * Solo SUPER_ADMIN / MARKETING.
 */
export function BroadcastPage() {
  const { user } = useAuth();
  const { message } = App.useApp();
  const library = useDoohMedia();
  const broadcast = useDoohBroadcast();
  const [publishing, setPublishing] = useState(false);

  const canPublish = canManageDoohMedia(user?.role);
  const loading = library.loading || broadcast.loading;

  const publish = async (body: SetDoohBroadcastBody) => {
    setPublishing(true);
    try {
      await doohApi.setBroadcast(body);
      message.success('Emisión global publicada; se aplicará en la próxima actualización de las pantallas.');
      broadcast.refresh();
    } catch (caught) {
      message.error(
        caught instanceof ApiError ? caught.message : 'No se pudo publicar la emisión global.',
      );
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="dooh-page">
      <div className="dooh-page__header">
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Emisión global
          </Typography.Title>
          <Typography.Text type="secondary">
            {broadcast.broadcast
              ? `${broadcast.broadcast.name} · ${formatItemCount(
                  broadcast.broadcast.items.length,
                )} · actualizada ${formatDateTime(broadcast.broadcast.updatedAt)}`
              : 'Crea la playlist que se reproducirá en todas las pantallas de la red.'}
          </Typography.Text>
        </div>
      </div>

      <DoohFlowHint current="broadcast" />

      {!canPublish && (
        <Alert
          type="warning"
          showIcon
          message="Solo lectura"
          description="Tu rol no permite publicar la emisión. Se requiere SuperAdmin o Marketing."
        />
      )}

      {broadcast.error && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la emisión global"
          description={broadcast.error}
          action={
            <Button size="small" onClick={broadcast.refresh}>
              Reintentar
            </Button>
          }
        />
      )}

      <Card>
        {loading ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
            <Spin size="large" />
          </div>
        ) : (
          <BroadcastComposer
            media={library.data}
            broadcast={broadcast.broadcast}
            publishing={publishing}
            canPublish={canPublish}
            onPublish={publish}
          />
        )}
      </Card>
    </div>
  );
}
